import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, Part } from "@/components/ui/box.schema";
import { facultyApi } from "@/lib/api/faculty";
import { receiptRow, type ImportBatchDetail, type ImportReceipt, type LearnerChoice } from "@/lib/api/imports";
import { personChoice } from "@/lib/person-name";
import {
  ROW_FIELDS, correctionRows, dataOf, fieldProblems, fixableReceipts, instructorChoiceOf, instructorData, instructorName,
  otherFields, rowsDigest, valuesOf, type RowField, type RowFix,
} from "@/lib/refused-rows";
import { importsWords as iw } from "./imports-copy";
import { RowFields } from "./RowFields";
import { refusedText as text, refusedWords as words } from "./refused-rows-copy";
import { useImportTask, type ImportSource } from "./useImportTask";

interface RowEdit { values?: Record<string, string>; instructor?: string; leaveOut?: boolean }
type Edits = Record<string, RowEdit>;
interface RowState { values: Record<string, string>; instructor: string; leaveOut: boolean }

/** A correction source carries its rows' learner choices, for the review to start from where the registrar has made none. */
export interface CorrectionSource extends ImportSource { choices: Record<string, LearnerChoice> }

const INSTRUCTOR_NAME: readonly RowField[] = [{ name: "display_name", type: "text", required: true }];

/**
 * An applied run's refused rows, fixed in place and sent again. The fixed rows
 * and the rows they depend on become one correction source, which the import
 * task reviews and applies like any other: its own dry run first, under its own
 * commit key, and its result names the new run. Null when nothing can be fixed here.
 */
export function useRefusedRows(batchId: string | null, detail: ImportBatchDetail | undefined): Box | null {
  const id = `run-fix:${batchId ?? "none"}`;
  const part = `${id}-source`;
  const kind = detail?.batch.kind ?? "historical";
  const receipts = detail?.receipts ?? [];
  const fixable = detail?.batch.mode === "apply" ? fixableReceipts(receipts) : [];
  const edits = useTabDraft<Edits>({ box: `${id}-rows`, record: "rows", version: null, initial: {} });
  const faculty = useQuery({ queryKey: ["import-faculty-records"], queryFn: () => facultyApi.list(), enabled: fixable.some((r) => r.entity_kind === "faculty") });

  const fieldsOf = (r: ImportReceipt) => r.entity_kind === "faculty" ? INSTRUCTOR_NAME : ROW_FIELDS[kind][r.entity_kind] ?? [];
  const recorded = (r: ImportReceipt) => receiptRow(r)?.data ?? {};
  const stateOf = (r: ImportReceipt): RowState => {
    const edit = edits.value[r.id] ?? {};
    const data = recorded(r);
    const prefill = r.entity_kind === "faculty" ? { display_name: instructorName(receiptRow(r)!) } : valuesOf(fieldsOf(r), data);
    return { values: { ...prefill, ...edit.values }, instructor: edit.instructor ?? instructorChoiceOf(data), leaveOut: edit.leaveOut ?? false };
  };
  const fixes: Record<string, RowFix> = Object.fromEntries(fixable.map((r) => {
    const s = stateOf(r);
    return [r.id, { leaveOut: s.leaveOut, data: r.entity_kind === "faculty" ? instructorData(s.instructor, s.values.display_name ?? "") : dataOf(fieldsOf(r), s.values, recorded(r)) }];
  }));
  const built = correctionRows(detail?.batch.source_name ?? "", kind, receipts, fixes);
  const material = JSON.stringify([built.name, built.rows, built.choices]);
  const sending = fixable.filter((r) => !fixes[r.id].leaveOut);

  const blockers = (): Blocker[] => {
    if (!sending.length) return [{ kind: "missing", part, sentence: words.keepOneRow }];
    return sending.flatMap((r): Blocker[] => {
      const s = stateOf(r);
      if (r.entity_kind === "faculty" && !s.instructor) return [{ kind: "missing", part, sentence: text.instructor(r.import_key) }];
      const fields = r.entity_kind === "faculty" && s.instructor !== "create" ? [] : fieldsOf(r);
      return fieldProblems(fields, s.values).map((p) => ({ kind: "missing", part,
        sentence: p.problem === "required" ? text.required(p.field, r.entity_kind, r.import_key) : text.number(p.field, r.entity_kind, r.import_key) }));
    });
  };
  const edit = (r: ImportReceipt, change: (was: RowEdit) => RowEdit) => edits.set((v) => ({ ...v, [r.id]: change(v[r.id] ?? {}) }));
  const setValue = (r: ImportReceipt, name: string, value: string) => edit(r, (was) => ({ ...was, values: { ...was.values, [name]: value } }));

  const waiting = blockers().length > 0;
  const rowsPart: Part = { id: part, label: words.refusedRows, owner: { system: "imam" }, fold: "never", draft: "browser", keep: edits,
    state: { class: waiting ? "waiting" : "draft", word: sending.length ? text.detail(sending.length, fixable.length - sending.length) : words.everyRowLeftOut },
    render: () => <div className="grid gap-4 text-ui">
      <p>{words.rowsIntro}</p>
      {built.parents > 0 && <p className="text-muted-foreground">{text.parents(built.parents)}</p>}
      {fixable.map((r) => {
        const s = stateOf(r);
        const name = instructorName(receiptRow(r)!);
        return <fieldset key={r.id} className="grid gap-3 border-t border-rule pt-3">
          <legend className="font-medium">{text.row(r.entity_kind, r.import_key)}</legend>
          {r.reason && <p className="text-muted-foreground">{r.reason}</p>}
          <label className="flex items-center gap-2"><input type="checkbox" checked={s.leaveOut} onChange={(e) => edit(r, (was) => ({ ...was, leaveOut: e.target.checked }))} />{words.leaveOut}</label>
          {!s.leaveOut && (r.entity_kind === "faculty" ? <>
            {faculty.isPending && <p role="status">{iw.readingFaculty}</p>}
            {faculty.isError && <p role="alert">{iw.facultyCouldNotBeRead} <button type="button" onClick={() => void faculty.refetch()}>{iw.tryAgain}</button></p>}
            <label className="grid gap-1">{iw.instructorRecordFor} {name}<select value={s.instructor} onChange={(e) => edit(r, (was) => ({ ...was, instructor: e.target.value }))}>
              <option value="">{iw.chooseRecord}</option>
              <option value="create">{iw.createAccountless}</option>
              {s.instructor && s.instructor !== "create" && !faculty.data?.some((f) => f.id === s.instructor) && <option value={s.instructor}>{s.instructor}</option>}
              {(faculty.data ?? []).filter((f) => !f.archived_at || f.id === s.instructor).map((f) => <option key={f.id} value={f.id} disabled={Boolean(f.archived_at)}>
                {personChoice(f.display_name, f.email)}{f.archived_at ? iw.archivedSuffix : ""}</option>)}
            </select></label>
            {s.instructor === "create" && <RowFields fields={INSTRUCTOR_NAME} values={s.values} onChange={(field, value) => setValue(r, field, value)} />}
          </> : <RowFields fields={fieldsOf(r)} others={otherFields(fieldsOf(r), recorded(r))} values={s.values} onChange={(field, value) => setValue(r, field, value)} />)}
        </fieldset>;
      })}
    </div> };

  const task = useImportTask(id, words.fixRefusedRows, kind, () => null,
    { parts: [rowsPart], sourceBlockers: blockers, material, emptyDetail: text.detail(sending.length, fixable.length - sending.length) });
  const result = task.box.kind === "task" ? task.box.result.state : "none";
  const latest = useRef({ task, built, id, kept: edits.kept, result, fixable: fixable.length });
  latest.current = { task, built, id, kept: edits.kept, result, fixable: fixable.length };
  useEffect(() => {
    const { built: rows, id: scope, kept, result: shown, fixable: count } = latest.current;
    // A confirmed resend ended the fixes it sent; only a new fix starts another correction source.
    if (!count || (shown === "confirmed" && !kept)) return;
    let active = true;
    void rowsDigest(rows.rows).then((digest) => {
      if (!active) return;
      const source: CorrectionSource = { name: rows.name, digest, rows: rows.rows, problems: [], scope, material, choices: rows.choices };
      latest.current.task.setSource(source);
    });
    return () => { active = false; };
  }, [material]);
  return fixable.length ? task.box : null;
}
